'use client';

import { forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { ModalRootContext } from './context/modal-root.context';
import { modalBodyStyles } from './styles/modal-body.styles';
import type { ModalBodyPropsInterface } from './types/modal-body.types';

/**
 * @since 10.0.0
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyPropsInterface>(
  (props: ModalBodyPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { modal } = useContext(ModalRootContext);
    const id = useId();
    const styles = css(modalBodyStyles, cssProp);

    useIsomorphicLayoutEffect(() => {
      modal.setDescriptionId(id);
      return () => modal.setDescriptionId(undefined);
    }, [id, modal.setDescriptionId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Body"
        id={id}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ModalBody.displayName = 'Modal.Body';
